// src/components/InTaxBaseComponent/utils/configIndex.js
/**
 * Build a fast lookup structure from pp_income_tax_config docs.
 * Robust to multiple docs: concatenates and deduplicates countries/years/tax items.
 */

// Canada province/territory code -> label as used in system config
const CA_REGION_NAME_BY_CODE = {
  ab: "Alberta Provincial Tax",
  bc: "British Columbia Provincial Tax",
  mb: "Manitoba Provincial Tax",
  nb: "New Brunswick Provincial Tax",
  nl: "Newfoundland and Labrador Provincial Tax",
  ns: "Nova Scotia Provincial Tax",
  nt: "Northwest Territories Tax",
  nu: "Nunavut Tax",
  on: "Ontario Provincial Tax",
  pe: "Prince Edward Island Provincial Tax",
  qc: "Quebec Provincial Tax",
  sk: "Saskatchewan Provincial Tax",
  yt: "Yukon Tax",
};

// US state code -> label pattern used in system config
const US_STATE_NAME_BY_CODE = {
  ca: "California State Tax",
  ny: "New York State Tax",
  tx: "Texas State Tax",
};

export function buildConfigIndex(rawDocs = []) {
  const unified = unifyDocs(rawDocs);

  const countries = Array.isArray(unified?.countries) ? unified.countries : [];
  const countryByCode = {};
  for (const c of countries) {
    countryByCode[String(c.code || "").toLowerCase()] = c;
  }

  function listYearsForCountry(code) {
    const c = countryByCode[String(code || "").toLowerCase()];
    return Array.isArray(c?.years) ? c.years.map((y) => y.year).sort((a, b) => b - a) : [];
  }

  function listRegionsForCountry(code) {
    const lower = String(code || "").toLowerCase();
    if (lower === "ca") return Object.keys(CA_REGION_NAME_BY_CODE);
    if (lower === "us") return Object.keys(US_STATE_NAME_BY_CODE);
    return [];
  }

  function regionLabel(code, regionCode) {
    const c = String(code || "").toLowerCase();
    const r = String(regionCode || "").toLowerCase();
    if (c === "ca") return CA_REGION_NAME_BY_CODE[r] ?? null;
    if (c === "us") return US_STATE_NAME_BY_CODE[r] ?? null;
    return null;
  }

  function getYearNode(code, year) {
    const c = countryByCode[String(code || "").toLowerCase()];
    const years = Array.isArray(c?.years) ? c.years : [];
    return years.find((y) => Number(y.year) === Number(year)) || null;
  }

  function pickBrackets({ countryCode, regionCode, filingStatus, year }) {
    const node = getYearNode(countryCode, year);
    if (!node) return { federal: null, regional: null };

    const cc = String(countryCode || "").toLowerCase();

    if (cc === "ca") {
      const federal = node.taxes.find((t) => String(t.type) === "federal") || null;
      const label = regionLabel("ca", regionCode);
      const regional =
        node.taxes.find(
          (t) =>
            (t.type === "provincial" || t.type === "territorial") &&
            (t.name === label)
        ) || null;
      return { federal, regional };
    }

    if (cc === "us") {
      const fs = String(filingStatus || "single").toLowerCase();
      const fedType = fs === "joint" || fs === "married_joint" ? "federal_joint" : "federal_single";
      const federal = node.taxes.find((t) => t.type === fedType) || null;

      const stLabel = US_STATE_NAME_BY_CODE[String(regionCode || "").toLowerCase()];
      const regional =
        node.taxes.find((t) => t.type === "state" && t.name?.startsWith(stLabel)) || null;

      return { federal, regional };
    }

    return { federal: null, regional: null };
  }

  function pickOtherDeductions({ countryCode, year }) {
    const node = getYearNode(countryCode, year);
    return Array.isArray(node?.other_deductions) ? node.other_deductions : [];
  }

  return {
    raw: unified,
    countries,
    listYearsForCountry,
    listRegionsForCountry,
    regionLabel,
    pickBrackets,
    pickOtherDeductions,
  };
}

/* ------------------------------ Merge helpers ------------------------------ */

function unifyDocs(docs) {
  // Accepts either:
  //  - one doc with { countries: [...] }
  //  - multiple docs each with { countries: [...] } or even single-country shapes
  const outByCountry = {}; // code -> { code, name, flag?, years: [...] }

  for (const d of docs || []) {
    if (!d) continue;

    // case A: doc has countries array
    if (Array.isArray(d.countries)) {
      for (const c of d.countries) mergeCountry(outByCountry, c);
      continue;
    }

    // case B: doc looks like a single country (defensive)
    if (d.code && Array.isArray(d.years)) {
      mergeCountry(outByCountry, d);
    }
  }

  return { countries: Object.values(outByCountry) };
}

function mergeCountry(bucket, incoming) {
  if (!incoming) return;
  const code = String(incoming.code || "").toLowerCase();
  if (!code) return;

  const existing = bucket[code] || {
    code,
    name: incoming.name ?? code.toUpperCase(),
    flag: incoming.flag ?? "",
    years: [],
  };
  // prefer first non-empty flag/name, but keep existing if already set
  if (!existing.name && incoming.name) existing.name = incoming.name;
  if (!existing.flag && incoming.flag) existing.flag = incoming.flag;

  existing.years = mergeYears(existing.years, incoming.years || []);
  bucket[code] = existing;
}

function mergeYears(baseYears = [], addYears = []) {
  const byYear = new Map(baseYears.map((y) => [Number(y.year), y]));
  for (const y of addYears) {
    const key = Number(y.year);
    const prev = byYear.get(key);
    if (!prev) {
      byYear.set(key, normalizeYear(y));
    } else {
      // merge taxes + other_deductions
      const merged = {
        year: key,
        taxes: mergeTaxes(prev.taxes || [], y.taxes || []),
        other_deductions: mergeByName(prev.other_deductions || [], y.other_deductions || []),
      };
      byYear.set(key, merged);
    }
  }
  return Array.from(byYear.values()).sort((a, b) => b.year - a.year);
}

function normalizeYear(y) {
  return {
    year: Number(y.year),
    taxes: Array.isArray(y.taxes) ? y.taxes.slice() : [],
    other_deductions: Array.isArray(y.other_deductions) ? y.other_deductions.slice() : [],
  };
}

function mergeTaxes(a = [], b = []) {
  // Deduplicate by (type, name) combo — last one wins
  const key = (t) => `${t.type ?? ""}::${t.name ?? ""}`;
  const map = new Map();
  for (const t of a) map.set(key(t), t);
  for (const t of b) map.set(key(t), t);
  return Array.from(map.values());
}

function mergeByName(a = [], b = []) {
  const key = (o) => String(o?.name ?? "");
  const map = new Map();
  for (const t of a) map.set(key(t), t);
  for (const t of b) map.set(key(t), t);
  return Array.from(map.values());
}
